import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import { MarketplaceProvider, useMarketplace } from './MarketplaceContext';
import { FavoritesProvider, useFavorites, favKey } from './FavoritesContext';
import { MarketplaceCard } from './MarketplaceCard';
import { TYPE_META } from './typeMetadata';
import { TypeIcon } from './TypeIcon';
import { ItemType, MarketplaceItem } from './types';

const MARKETPLACE_TYPES = Object.keys(TYPE_META) as ItemType[];

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    background: theme.palette.type === 'dark' ? '#0d1117' : '#f4f6f8',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 24px',
    background: theme.palette.type === 'dark' ? '#061A38' : '#061A38',
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? '#30363d' : 'rgba(255,255,255,0.08)'}`,
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 6,
    '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' },
  },
  crumbSep: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: '0.78rem',
  },
  crumbCurrent: {
    color: '#fff',
    fontSize: '0.78rem',
    fontWeight: 700,
  },
  hero: {
    background: 'linear-gradient(160deg, #061A38 0%, #0D3E86 35%, #0057BF 70%, #3D8EDB 100%)',
    padding: '32px 28px 28px',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""', position: 'absolute', inset: 0,
      background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.02\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      pointerEvents: 'none',
    },
  },
  heroTitle: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '-0.02em',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  heroSub: {
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
    marginTop: 4,
  },
  body: {
    padding: '24px 28px 48px',
    maxWidth: 1400,
    margin: '0 auto',
  },
  sectionHeader: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  sectionDivider: {
    margin: '24px 0',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(0,0,0,0.06)',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '64px 24px',
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  emptyIcon: {
    fontSize: '3rem',
    color: theme.palette.text.disabled,
    marginBottom: 12,
  },
}));

function FavoritesPageInner() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { items, loading: itemsLoading } = useMarketplace();
  const { favorites, loading: favsLoading } = useFavorites();

  const loading = itemsLoading || favsLoading;

  // Build a map for quick lookup
  const itemMap = useMemo(() => {
    const m = new Map<string, MarketplaceItem>();
    for (const item of items) {
      m.set(favKey(item.name, item.type), item);
    }
    return m;
  }, [items]);

  // Resolve favorites to actual marketplace items, grouped by type
  const sections = useMemo(() => {
    const favItems: MarketplaceItem[] = [];
    for (const key of favorites) {
      const item = itemMap.get(key);
      if (item) favItems.push(item);
    }

    return MARKETPLACE_TYPES
      .map(type => ({
        type,
        items: favItems.filter(i => i.type === type),
      }))
      .filter(s => s.items.length > 0);
  }, [favorites, itemMap]);

  const totalFavs = sections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <Box className={classes.root}>
      {/* Top bar */}
      <Box className={classes.topBar}>
        <button className={classes.backBtn} onClick={() => navigate('/ai-marketplace')}>
          <ArrowBackIcon style={{ fontSize: '0.875rem' }} /> AI Marketplace
        </button>
        <span className={classes.crumbSep}>/</span>
        <Typography className={classes.crumbCurrent}>Favorites</Typography>
      </Box>

      {/* Hero */}
      <Box className={classes.hero}>
        <Box style={{ position: 'relative', zIndex: 1 }}>
          <Typography className={classes.heroTitle}>
            <BookmarkIcon style={{ fontSize: '1.6rem', color: '#FFB020' }} />
            My Favorites
          </Typography>
          <Typography className={classes.heroSub}>
            {loading ? 'Loading…' : `${totalFavs} saved item${totalFavs !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box className={classes.body}>
        {loading && (
          <Box style={{ textAlign: 'center', padding: 48 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && totalFavs === 0 && (
          <Box className={classes.emptyState}>
            <BookmarkIcon className={classes.emptyIcon} />
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 6 }}>
              No favorites yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Click the bookmark icon on any marketplace card to save it here.
            </Typography>
          </Box>
        )}

        {!loading && sections.map(({ type, items: sectionItems }, idx) => {
          const meta = TYPE_META[type];
          return (
            <Box key={type}>
              {idx > 0 && <Divider className={classes.sectionDivider} />}
              <Box
                className={classes.sectionHeader}
                style={{ marginTop: idx > 0 ? 32 : 0 }}
              >
                <Typography
                  className={classes.sectionTitle}
                  component="div"
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <TypeIcon
                    iconName={meta.iconName}
                    fontSize="small"
                    style={{ color: meta.color }}
                  />
                  &nbsp;{meta.label}s
                  <Chip
                    label={sectionItems.length}
                    size="small"
                    style={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      height: 18,
                      minWidth: 20,
                      position: 'relative',
                      top: 4,
                    }}
                  />
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {sectionItems.map(item => (
                  <Grid item xs={12} sm={6} md={4} xl={3} key={item.id}>
                    <MarketplaceCard item={item} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function FavoritesPage() {
  return (
    <MarketplaceProvider>
      <FavoritesProvider>
        <FavoritesPageInner />
      </FavoritesProvider>
    </MarketplaceProvider>
  );
}
